import { useAuth } from '@/lib/auth/auth';
import {
  applyRsvpMutation,
  applyRsvpRemoval,
  applySavedMutation,
  followingFeedKeys,
  nearbyFeedKeys,
} from '@/features/events/feed/feedQuery';
import { updateCitywideQueries } from '@/features/events/feed/feedMutationCache';
import { citywideEventKeys } from '@/features/events/feed/useCitywideEvents';
import { invalidateEventQueries } from '@/features/events/eventMutationCache';
import { getEventCampaignAttribution } from '@/features/events/eventCampaignAttribution';
import { useWritableMarketMutation } from '@/features/market/useWritableMarketMutation';
import type {
  CreateRSVPRequest,
  CreateEventFeedbackRequest,
  CreateSavedEventRequest,
  FeedEvent,
  FeedResponse,
  RSVPRecord,
  RSVPVisibility,
} from '@/lib/api/apiTypes';
import { Platform } from 'react-native';
import {
  InfiniteData,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

type QuerySnapshot<T> = [QueryKey, T | undefined][];

type MutationContext = {
  nearby: QuerySnapshot<InfiniteData<FeedResponse, number>>;
  following: QuerySnapshot<InfiniteData<FeedEvent[], number>>;
  citywide: QuerySnapshot<FeedEvent[]>;
};

export type FeedMutationScope = 'nearby' | 'following' | 'citywide' | 'all';

type FeedMutationOptions = {
  refetchOnRsvpSettled?: boolean;
};

export function useEventFeedMutations(
  scope: FeedMutationScope = 'all',
  options: FeedMutationOptions = {}
) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();
  const refetchOnRsvpSettled = options.refetchOnRsvpSettled ?? true;

  const toggleSaved = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ event }: { event: FeedEvent }) => {
        if (event.is_saved) {
          await request(`/saved-events/${event.id}`, {
            method: 'DELETE',
          });
          return false;
        }

        await request('/saved-events', {
          method: 'POST',
          body: JSON.stringify({
            event_id: event.id,
            ...(await getEventCampaignAttribution(event.id)),
          } satisfies CreateSavedEventRequest),
        });
        return true;
      },
      onMutate: async ({ event }: { event: FeedEvent }) => {
        await cancelScopedQueries(queryClient, scope);

        const context = snapshotFeedQueries(queryClient, scope);

        updateScopedQueries(queryClient, scope, {
          nearby: (item) => applySavedMutation(item, event.id, !event.is_saved),
          following: (item) => applySavedMutation(item, event.id, !event.is_saved),
          citywide: (item) => applySavedMutation(item, event.id, !event.is_saved),
        });

        return context;
      },
      onError: (
        _error: unknown,
        _variables: { event: FeedEvent },
        context: MutationContext | undefined
      ) => restoreFeedQueries(queryClient, context, scope),
      onSettled: async (_data, _error, variables) => {
        await invalidateEventQueries(queryClient, variables.event.id, 'inactive');
      },
    })
  );

  const confirmGoing = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({
        event,
        visibility,
        source,
      }: {
        event: FeedEvent;
        visibility: RSVPVisibility;
        source: CreateRSVPRequest['source'];
      }) => {
        const attribution = await getEventCampaignAttribution(event.id);
        const payload: CreateRSVPRequest = {
          event_id: event.id,
          status: 'going',
          visibility,
          source,
          surface: Platform.OS === 'web' ? 'web' : 'mobile_feed',
          ...attribution,
        };

        return request<RSVPRecord>('/rsvp', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      onMutate: async ({
        event,
      }: {
        event: FeedEvent;
        visibility: RSVPVisibility;
        source: CreateRSVPRequest['source'];
      }) => {
        await cancelScopedQueries(queryClient, scope);

        const context = snapshotFeedQueries(queryClient, scope);

        updateScopedQueries(queryClient, scope, {
          nearby: (item) => applyRsvpMutation(item, event.id, 'going'),
          following: (item) => applyRsvpMutation(item, event.id, 'going'),
          citywide: (item) => applyRsvpMutation(item, event.id, 'going'),
        });

        return context;
      },
      onError: (
        _error: unknown,
        _variables: {
          event: FeedEvent;
          visibility: RSVPVisibility;
          source: CreateRSVPRequest['source'];
        },
        context: MutationContext | undefined
      ) => restoreFeedQueries(queryClient, context, scope),
      onSettled: async () => {
        await invalidateScopedQueries(queryClient, scope, {
          refetchActive: refetchOnRsvpSettled,
        });
      },
    })
  );

  const removeRsvp = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID }: { eventID: string }) => {
        const surface = Platform.OS === 'web' ? 'web' : 'mobile_feed';
        return request(`/events/${eventID}/rsvp?source=feed&surface=${surface}`, {
          method: 'DELETE',
        });
      },
      onMutate: async ({ eventID }: { eventID: string }) => {
        await cancelScopedQueries(queryClient, scope);

        const context = snapshotFeedQueries(queryClient, scope);

        updateScopedQueries(queryClient, scope, {
          nearby: (item) => applyRsvpRemoval(item, eventID),
          following: (item) => applyRsvpRemoval(item, eventID),
          citywide: (item) => applyRsvpRemoval(item, eventID),
        });

        return context;
      },
      onError: (
        _error: unknown,
        _variables: { eventID: string },
        context: MutationContext | undefined
      ) => restoreFeedQueries(queryClient, context, scope),
      onSettled: async () => {
        await invalidateScopedQueries(queryClient, scope, {
          refetchActive: refetchOnRsvpSettled,
        });
      },
    })
  );

  const markNotInterested = useMutation({
    mutationFn: async ({ eventID }: { eventID: string }) => {
      await request(`/events/${eventID}/feedback`, {
        method: 'POST',
        body: JSON.stringify({
          feedback: 'not_interested',
        } satisfies CreateEventFeedbackRequest),
      });
    },
  });

  const undoNotInterested = useMutation({
    mutationFn: async ({ eventID }: { eventID: string }) => {
      await request(`/events/${eventID}/feedback/not_interested`, {
        method: 'DELETE',
      });
    },
  });

  return {
    toggleSaved,
    confirmGoing,
    removeRsvp,
    markNotInterested,
    undoNotInterested,
  };
}

function snapshotFeedQueries(queryClient: QueryClient, scope: FeedMutationScope): MutationContext {
  // Optimistic feed updates span three cache shapes: nearby pages, following pages, and citywide arrays.
  return {
    nearby:
      scope === 'all' || scope === 'nearby'
        ? queryClient.getQueriesData<InfiniteData<FeedResponse, number>>({
            queryKey: nearbyFeedKeys.all,
          })
        : [],
    following:
      scope === 'all' || scope === 'following'
        ? queryClient.getQueriesData<InfiniteData<FeedEvent[], number>>({
            queryKey: followingFeedKeys.all,
          })
        : [],
    citywide:
      scope === 'all' || scope === 'citywide'
        ? queryClient.getQueriesData<FeedEvent[]>({
            queryKey: citywideEventKeys.all,
          })
        : [],
  };
}

function restoreFeedQueries(
  queryClient: QueryClient,
  context: MutationContext | undefined,
  scope: FeedMutationScope
) {
  if (!context) {
    return;
  }

  if (scope === 'all' || scope === 'nearby') {
    for (const [queryKey, data] of context.nearby) {
      queryClient.setQueryData(queryKey, data);
    }
  }

  if (scope === 'all' || scope === 'following') {
    for (const [queryKey, data] of context.following) {
      queryClient.setQueryData(queryKey, data);
    }
  }

  if (scope === 'all' || scope === 'citywide') {
    for (const [queryKey, data] of context.citywide) {
      queryClient.setQueryData(queryKey, data);
    }
  }
}

async function cancelScopedQueries(queryClient: QueryClient, scope: FeedMutationScope) {
  const tasks: Promise<unknown>[] = [];
  if (scope === 'all' || scope === 'nearby') {
    tasks.push(queryClient.cancelQueries({ queryKey: nearbyFeedKeys.all }));
  }
  if (scope === 'all' || scope === 'following') {
    tasks.push(queryClient.cancelQueries({ queryKey: followingFeedKeys.all }));
  }
  if (scope === 'all' || scope === 'citywide') {
    tasks.push(queryClient.cancelQueries({ queryKey: citywideEventKeys.all }));
  }
  await Promise.all(tasks);
}

async function invalidateScopedQueries(
  queryClient: QueryClient,
  scope: FeedMutationScope,
  options: { refetchActive?: boolean } = {}
) {
  const refetchType = options.refetchActive === false ? 'none' : 'active';
  const tasks: Promise<unknown>[] = [];
  if (scope === 'all' || scope === 'nearby') {
    tasks.push(queryClient.invalidateQueries({ queryKey: nearbyFeedKeys.all, refetchType }));
  }
  if (scope === 'all' || scope === 'following') {
    tasks.push(queryClient.invalidateQueries({ queryKey: followingFeedKeys.all, refetchType }));
  }
  if (scope === 'all' || scope === 'citywide') {
    tasks.push(queryClient.invalidateQueries({ queryKey: citywideEventKeys.all, refetchType }));
  }
  await Promise.all(tasks);
}

function updateScopedQueries(
  queryClient: QueryClient,
  scope: FeedMutationScope,
  updaters: {
    nearby: (event: FeedEvent) => FeedEvent;
    following: (event: FeedEvent) => FeedEvent;
    citywide: (event: FeedEvent) => FeedEvent;
  }
) {
  if (scope === 'all' || scope === 'nearby') {
    updateNearbyQueries(queryClient, updaters.nearby);
  }
  if (scope === 'all' || scope === 'following') {
    updateFollowingQueries(queryClient, updaters.following);
  }
  if (scope === 'all' || scope === 'citywide') {
    updateCitywideQueries(queryClient, updaters.citywide);
  }
}

function updateNearbyQueries(
  queryClient: QueryClient,
  updateEvent: (event: FeedEvent) => FeedEvent
) {
  queryClient.setQueriesData<InfiniteData<FeedResponse, number>>(
    { queryKey: nearbyFeedKeys.all },
    (data: InfiniteData<FeedResponse, number> | undefined) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page: FeedResponse) => ({
              ...page,
              events: page.events.map(updateEvent),
              items: page.items?.map((item) =>
                item.type === 'event' && item.event
                  ? {
                      ...item,
                      event: updateEvent(item.event),
                    }
                  : item
              ),
            })),
          }
        : data
  );
}

function updateFollowingQueries(
  queryClient: QueryClient,
  updateEvent: (event: FeedEvent) => FeedEvent
) {
  queryClient.setQueriesData<InfiniteData<FeedEvent[], number>>(
    { queryKey: followingFeedKeys.all },
    (data: InfiniteData<FeedEvent[], number> | undefined) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page: FeedEvent[]) => page.map(updateEvent)),
          }
        : data
  );
}
